import { useNow } from '@/hooks/useNow';
import { cn } from '@/lib/cn';
import { formatRelative } from '@/lib/formatters';

interface LiveIndicatorProps {
  /** Timestamp of the last reading in the sheet. Staleness is measured from
   *  THIS, not from when the app last polled. */
  lastDataAt: Date | null;
  /** Go offline once the newest reading is older than this. Default 6 min. */
  offlineAfterSeconds?: number;
  className?: string;
}

export function LiveIndicator({
  lastDataAt,
  offlineAfterSeconds = 360, // 6 min
  className,
}: LiveIndicatorProps) {
  // Re-render every 15s so the "x ago" string and the online/offline flip
  // stay current even though lastDataAt itself is frozen.
  useNow(15_000);

  if (!lastDataAt) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-[11px] text-ink-400', className)}>
        <span className="w-1.5 h-1.5 rounded-full bg-ink-300" />
        Connecting…
      </span>
    );
  }

  const ageSeconds = (Date.now() - lastDataAt.getTime()) / 1000;
  const isOffline  = ageSeconds > offlineAfterSeconds;
  const label      = formatRelative(lastDataAt);

  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 text-[11px] font-medium',
      isOffline ? 'text-signal-warn' : 'text-ink-500',
      className,
    )}>
      <span
        className={cn(
          'w-1.5 h-1.5 rounded-full',
          isOffline ? 'bg-signal-warn' : 'bg-signal-good animate-pulse-dot',
        )}
      />
      {isOffline ? 'Offline · ' : 'Live · '}{label}
    </span>
  );
}